import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Phone } from 'lucide-react';
import { Fragment, type ReactNode, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { StatCard } from '@/components/ui/stat-card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { api } from '@/lib/api';
import { useFilters } from '@/lib/filters';
import { formatIsoDate, formatMetric } from '@/lib/format';
import { useViewMessages } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/*
 * Leads — the lead → booking loop. Two jobs in one page:
 *   1) THE decision number: how many captured leads become booked prøvetimer
 *      (quiz vs everything else) — what the quiz-vs-LP call rests on.
 *   2) The operational call list: who left their details, who asked to be
 *      called, who has not booked yet — with tap-to-call phone links.
 *
 * One row per person (server-side identity dedupe), so nobody is chased twice.
 */

const MESSAGES = {
  nb: {
    title: 'Leads',
    sub: (from: string, to: string) =>
      `Fra lead til booket prøvetime, ${from} → ${to} — styres av datovelgeren øverst.`,
    statPeople: 'Personer',
    statPeopleSub: 'la igjen detaljene sine',
    statBooked: 'Ble booking',
    statBookedSub: (n: string) => `${n} har booket`,
    statMature: 'Rate (modne)',
    statMatureSub: (n: string) => `av ${n} leads eldre enn 48 t`,
    statMedian: 'Tid til booking',
    statMedianSub: 'median',
    warnCallbacks: (n: number) => `${n} har bedt om å bli oppringt og venter fortsatt`,
    liveOk: (t: string) => `Momence sjekket ${t}`,
    liveOkHelp:
      'Ringelista sjekkes mot Momence hvert femte minutt, så en booking som nettopp ble gjort er allerede med. Resten av tallene kommer fra timesskannet.',
    livePartial: (t: string) => `Momence sjekket ${t} — noen oppslag feilet`,
    livePartialHelp:
      'Sjekken kom gjennom, men Momence svarte ikke på alle. De som mangler prøves igjen om fem minutter. Sjekk i Momence før du ringer.',
    liveError: (t: string) => `Momence-sjekk feiler — sist OK ${t}`,
    liveErrorNever: 'Momence-sjekk feiler — har aldri lyktes',
    liveErrorHelp:
      'Den hyppige sjekken mot Momence feiler. Lista er ikke gal, men den kan være opptil en time gammel på bookinger. Sjekk i Momence før du ringer.',
    liveNever: 'Kun timesskann',
    liveNeverHelp:
      'Den hyppige Momence-sjekken har aldri kjørt her. Bookinger kan være opptil en time forsinket i denne lista.',
    warnMomenceUncertain: (n: number) =>
      `${n} lead(s) fikk ikke svar fra Momence — søk dem opp i Momence FØR du legger dem inn, ellers lager du dublett.`,
    warnMomenceMissing: (n: number) =>
      `${n} lead(s) nådde aldri Momence — de finnes bare her. Legg dem inn manuelt.`,
    warnTruncated: 'Perioden er større enn leseperioden — tallene dekker et avkortet utvalg.',
    warnCancelled: (n: number) => `${n} booking(er) ble avlyst og telles ikke som konvertering`,
    internalTitle: 'Interne / testleads',
    internalSub:
      'Staben og våre egne tester. Holdt utenfor alle tallene over — vises her for åpenhetens skyld.',
    priorBooking: 'Hadde booking fra før',
    doNotCallBadge: 'Ikke ring',
    noCallTitle: 'Ønsker ikke å bli ringt',
    noCallSub:
      'De svarte «Nei takk» på steg 8. De står her så du vet om dem — men ring dem ikke. De kan fortsatt booke selv.',
    warnDoNotCall: (n: number) =>
      `${n} av leadene har sagt nei til å bli oppringt — de er holdt utenfor ringelista`,
    sourceTitle: 'Hvor leadene kommer fra',
    sourceSub: 'Samme spørsmål per kilde: hvor mange av leadene ble en booking?',
    colSource: 'Kilde',
    colPeople: 'Personer',
    colBooked: 'Booket',
    colRate: 'Rate',
    srcQuiz: 'Quiz',
    srcMeta: 'Facebook',
    srcOther: 'Andre',
    waitingTitle: 'Ringeliste',
    waitingSub:
      'Har ikke booket ennå. Øverst: de som ba om å bli ringt, deretter de som har ventet lengst.',
    bookedTitle: 'Ble booking',
    bookedSub: 'Leads som endte med en booket prøvetime.',
    colName: 'Navn',
    colPhone: 'Telefon',
    colWaited: 'Ventet',
    colLead: 'Lead',
    colBooking: 'Booking',
    inMomenceBooked: 'Booket i Momence',
    showedUp: 'Møtte',
    noShow: 'Møtte ikke',
    becameMember: 'Ble medlem',
    expandRow: 'Utvid rad',
    callbackBadge: 'Vil bli ringt',
    momenceUncertain: 'Usikker i Momence',
    momenceUncertainHelp:
      'Momence svarte ikke da vi sendte henne (tidsavbrudd). Hun ble sannsynligvis opprettet likevel. Søk henne opp i Momence FØR du legger henne inn — ellers får du dublett.',
    momenceRejected: 'Avvist av Momence',
    momenceRejectedHelp:
      'Momence leste innsendingen og avviste den. Hun finnes bare hos oss — legg henne inn manuelt.',
    momenceNotSent: 'Ikke sendt',
    momenceNotSentHelp:
      'Vi fikk aldri sendt henne videre — feilen var hos oss. Hun finnes bare hos oss, så legg henne inn manuelt.',
    answersTitle: 'Svarene fra quizen',
    noAnswers: 'Ingen quiz-svar lagret på denne leaden.',
    submissionsN: (n: number) => `${n} innsendinger`,
    matchedByName: 'Koblet på navn — dobbeltsjekk at det er samme person',
    bookedBefore: 'Booket FØR leaden — trolig en eksisterende kunde',
    hoursShort: (n: number) => (n < 48 ? `${Math.round(n)} t` : `${Math.round(n / 24)} d`),
    cancelled: 'Avlyst',
    emptyTitle: 'Ingen leads i perioden',
    emptyHint: 'Leads dukker opp her når noen fyller ut kontaktsteget i quizen.',
  },
  en: {
    title: 'Leads',
    sub: (from: string, to: string) =>
      `From lead to booked trial, ${from} → ${to} — controlled by the date picker at the top.`,
    statPeople: 'People',
    statPeopleSub: 'left their details',
    statBooked: 'Became a booking',
    statBookedSub: (n: string) => `${n} have booked`,
    statMature: 'Rate (mature)',
    statMatureSub: (n: string) => `of ${n} leads older than 48h`,
    statMedian: 'Time to booking',
    statMedianSub: 'median',
    warnCallbacks: (n: number) => `${n} asked to be called and are still waiting`,
    liveOk: (t: string) => `Momence checked ${t}`,
    liveOkHelp:
      'The call list is checked against Momence every five minutes, so a booking just made is already here. The rest of the numbers come from the hourly scan.',
    livePartial: (t: string) => `Momence checked ${t} — some lookups failed`,
    livePartialHelp:
      'The check ran, but Momence did not answer for everyone. The rest are retried in five minutes. Check Momence before calling.',
    liveError: (t: string) => `Momence check failing — last OK ${t}`,
    liveErrorNever: 'Momence check failing — has never succeeded',
    liveErrorHelp:
      'The frequent Momence check is failing. The list is not wrong, but bookings may be up to an hour stale. Check Momence before calling.',
    liveNever: 'Hourly scan only',
    liveNeverHelp:
      'The frequent Momence check has never run here. Bookings may be up to an hour behind in this list.',
    warnMomenceUncertain: (n: number) =>
      `${n} lead(s) got no answer from Momence — look them up in Momence BEFORE adding them, or you will create a duplicate.`,
    warnMomenceMissing: (n: number) =>
      `${n} lead(s) never reached Momence — they exist only here. Add them manually.`,
    warnTruncated: 'The range exceeds the read window — these numbers cover a truncated sample.',
    warnCancelled: (n: number) => `${n} booking(s) were cancelled and do not count as conversions`,
    internalTitle: 'Internal / test leads',
    internalSub:
      'Staff and our own tests. Excluded from every number above — listed here for transparency.',
    priorBooking: 'Already had a booking',
    doNotCallBadge: 'Do not call',
    noCallTitle: 'Does not want to be called',
    noCallSub:
      'They answered «No thanks» on step 8. Listed so you know about them — but do not call. They can still book themselves.',
    warnDoNotCall: (n: number) => `${n} lead(s) declined a call — they are kept off the call list`,
    sourceTitle: 'Where the leads come from',
    sourceSub: 'The same question per source: how many leads became a booking?',
    colSource: 'Source',
    colPeople: 'People',
    colBooked: 'Booked',
    colRate: 'Rate',
    srcQuiz: 'Quiz',
    srcMeta: 'Facebook',
    srcOther: 'Other',
    waitingTitle: 'Call list',
    waitingSub: 'Not booked yet. Top: those who asked for a call, then those waiting longest.',
    bookedTitle: 'Became a booking',
    bookedSub: 'Leads that ended in a booked trial.',
    colName: 'Name',
    colPhone: 'Phone',
    colWaited: 'Waited',
    colLead: 'Lead',
    colBooking: 'Booking',
    inMomenceBooked: 'Booked in Momence',
    showedUp: 'Showed up',
    noShow: 'No-show',
    becameMember: 'Became a member',
    expandRow: 'Expand row',
    callbackBadge: 'Wants a call',
    momenceUncertain: 'Uncertain in Momence',
    momenceUncertainHelp:
      'Momence did not answer when we sent her (timeout). She was most likely created anyway. Look her up in Momence BEFORE adding her, or you will create a duplicate.',
    momenceRejected: 'Rejected by Momence',
    momenceRejectedHelp:
      'Momence read the submission and rejected it. She exists only with us — add her manually.',
    momenceNotSent: 'Never sent',
    momenceNotSentHelp:
      'We never managed to forward her — the failure was on our side. She exists only with us, so add her manually.',
    answersTitle: 'Quiz answers',
    noAnswers: 'No quiz answers stored on this lead.',
    submissionsN: (n: number) => `${n} submissions`,
    matchedByName: 'Matched on name — double-check it is the same person',
    bookedBefore: 'Booked BEFORE the lead — likely an existing customer',
    hoursShort: (n: number) => (n < 48 ? `${Math.round(n)}h` : `${Math.round(n / 24)}d`),
    cancelled: 'Cancelled',
    emptyTitle: 'No leads in this range',
    emptyHint: 'Leads appear here when someone completes the quiz contact step.',
  },
};

type Msgs = (typeof MESSAGES)['nb'];

type LeadsData = Awaited<ReturnType<typeof api.leads>>;
type PersonRow = LeadsData['people'][number];
type SourceRow = LeadsData['bySource'][number];

function useLeads(from: string, to: string) {
  return useQuery({
    queryKey: ['leads', from, to],
    queryFn: () => api.leads({ from, to }),
  });
}

const EM_DASH = '—';

// Pinned to Oslo like the slot times: a viewer in another timezone was reading
// lead times in their own clock next to Oslo appointment times.
const dateTimeFmt = new Intl.DateTimeFormat('nb-NO', {
  timeZone: 'Europe/Oslo',
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});
const slotFmt = new Intl.DateTimeFormat('nb-NO', {
  timeZone: 'Europe/Oslo',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

function fmtWhen(iso: string): string {
  try {
    return dateTimeFmt.format(new Date(iso));
  } catch {
    return iso.slice(0, 16);
  }
}
function fmtSlot(iso: string): string {
  try {
    return slotFmt.format(new Date(iso));
  } catch {
    return iso.slice(0, 16);
  }
}
function sourceLabel(m: Msgs, s: SourceRow['source']): string {
  if (s === 'quiz') return m.srcQuiz;
  if (s === 'meta') return m.srcMeta;
  return m.srcOther;
}
/** Discovery/summary string → label/value rows (newline or ' | ' separated). */
function parseAnswers(text: string): { key: string; label: string; value: string }[] {
  return text
    .split(/\n+|\s\|\s/)
    .filter((p) => p.trim())
    .map((part, i) => {
      const sep = part.indexOf(': ');
      return {
        key: `a-${i}-${part.slice(0, 24)}`,
        label: sep > 0 ? part.slice(0, sep) : '',
        value: sep > 0 ? part.slice(sep + 2) : part,
      };
    });
}

export function LeadsPage() {
  const m = useViewMessages(MESSAGES);
  const { from, to } = useFilters();
  const query = useLeads(from, to);

  if (query.isLoading)
    return (
      <PageFrame m={m} from={from} to={to}>
        <LoadingState />
      </PageFrame>
    );
  if (query.isError || !query.data)
    return (
      <PageFrame m={m} from={from} to={to}>
        <ErrorState onRetry={() => query.refetch()} />
      </PageFrame>
    );

  const data = query.data;
  const real = data.people.filter((p) => !p.internal);
  /**
   * Booked through ANY channel — our widget, the Momence widget, or the phone.
   * One predicate for every list on the page: three separate filters that each
   * only knew `p.booking` is how the KPI could say «5 har booket» above a
   * section headed «Ble booking (2)», and how three people who booked in
   * Momence fell out of the page entirely — removed from the call list without
   * ever landing anywhere else.
   */
  const hasBooking = (p: (typeof data.people)[number]) =>
    (!!p.booking && !p.booking.cancelled) || (!!p.momence && !p.momence.cancelled);
  const waiting = real.filter((p) => !hasBooking(p) && !p.doNotCall);
  const noCall = real.filter((p) => !hasBooking(p) && p.doNotCall);
  const booked = real.filter(hasBooking);
  const internal = data.people.filter((p) => p.internal);

  return (
    <PageFrame m={m} from={from} to={to}>
      {data.totals.people === 0 ? (
        <EmptyState title={m.emptyTitle}>{m.emptyHint}</EmptyState>
      ) : (
        <>
          <KpiRow m={m} totals={data.totals} />
          <Warnings m={m} totals={data.totals} truncated={data.truncated} />
          {data.bySource.length > 1 ? <SourceCard m={m} rows={data.bySource} /> : null}
          <LiveStamp m={m} live={data.totals.liveCheck} />
          <PeopleCard m={m} rows={waiting} title={m.waitingTitle} sub={m.waitingSub} />
          {booked.length > 0 ? (
            <PeopleCard m={m} rows={booked} title={m.bookedTitle} sub={m.bookedSub} collapsed />
          ) : null}
          {noCall.length > 0 ? (
            <PeopleCard m={m} rows={noCall} title={m.noCallTitle} sub={m.noCallSub} />
          ) : null}
          {internal.length > 0 ? (
            <PeopleCard
              m={m}
              rows={internal}
              title={m.internalTitle}
              sub={m.internalSub}
              collapsed
            />
          ) : null}
        </>
      )}
    </PageFrame>
  );
}

function PageFrame({
  m,
  from,
  to,
  children,
}: {
  m: Msgs;
  from: string;
  to: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-title text-fg">{m.title}</h1>
        <p className="mt-1 max-w-3xl text-body text-fg-muted">
          {m.sub(formatIsoDate(from).date, formatIsoDate(to).date)}
        </p>
      </header>
      {children}
    </div>
  );
}

function CardHeading({ title, sub }: { title: string; sub: string }) {
  return (
    <div>
      <h2 className="text-section text-fg">{title}</h2>
      <p className="mt-0.5 max-w-3xl text-label text-fg-muted">{sub}</p>
    </div>
  );
}

function KpiRow({ m, totals }: { m: Msgs; totals: LeadsData['totals'] }) {
  const median =
    totals.medianHoursToBook === null ? EM_DASH : m.hoursShort(totals.medianHoursToBook);
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        label={m.statPeople}
        source="lead"
        value={formatMetric(totals.people, 'int')}
        sub={m.statPeopleSub}
      />
      <StatCard
        label={m.statBooked}
        source="booking"
        value={formatMetric(totals.bookedPct, 'pct')}
        sub={m.statBookedSub(formatMetric(totals.booked, 'int'))}
      />
      <StatCard
        label={m.statMature}
        source="derived"
        value={formatMetric(totals.bookedPctMature, 'pct')}
        sub={m.statMatureSub(formatMetric(totals.maturePeople, 'int'))}
      />
      <StatCard label={m.statMedian} source="derived" value={median} sub={m.statMedianSub} />
    </div>
  );
}

function Warnings({
  m,
  totals,
  truncated,
}: {
  m: Msgs;
  totals: LeadsData['totals'];
  truncated: boolean;
}) {
  const items: string[] = [];
  if (totals.callbacksPending > 0) items.push(m.warnCallbacks(totals.callbacksPending));
  if (totals.momenceUncertain > 0) items.push(m.warnMomenceUncertain(totals.momenceUncertain));
  if (totals.momenceMissing > 0) items.push(m.warnMomenceMissing(totals.momenceMissing));
  if (totals.doNotCall > 0) items.push(m.warnDoNotCall(totals.doNotCall));
  if (totals.cancelledConversions > 0) items.push(m.warnCancelled(totals.cancelledConversions));
  if (truncated) items.push(m.warnTruncated);
  if (items.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {items.map((t) => (
        <div
          key={t}
          className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-label text-fg"
        >
          ⚠️ {t}
        </div>
      ))}
    </div>
  );
}

function SourceCard({ m, rows }: { m: Msgs; rows: SourceRow[] }) {
  return (
    <Card className="flex flex-col gap-3 p-3 sm:p-4">
      <CardHeading title={m.sourceTitle} sub={m.sourceSub} />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[24rem] text-label">
          <thead>
            <tr className="text-left text-micro text-fg-subtle">
              <th className="py-1.5 pr-3 font-medium">{m.colSource}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colPeople}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colBooked}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{m.colRate}</th>
              <th className="py-1.5 text-right font-medium">{m.statMature}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.source} className="border-t border-border">
                <td className="py-2 pr-3 text-fg">{sourceLabel(m, r.source)}</td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(r.people, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg">
                  {formatMetric(r.booked, 'int')}
                </td>
                <td className="py-2 pr-3 text-right tabular text-fg-subtle">
                  {formatMetric(r.bookedPct, 'pct')}
                </td>
                <td className="py-2 text-right tabular text-fg-subtle">
                  {formatMetric(r.bookedPctMature, 'pct')}
                  <span className="ml-1 text-micro text-fg-subtle">
                    ({formatMetric(r.maturePeople, 'int')})
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/**
 * How fresh the Momence side of this list is.
 *
 * The call list is used with a phone in hand, so "when was this last true"
 * is part of the data, not decoration. The three states read differently on
 * purpose: a page that shows the same thing whether it checked two minutes ago
 * or has not managed to check since dawn is making an unfounded claim, which is
 * exactly the mistake the «Ikke i Momence» badge made.
 */
function LiveStamp({
  m,
  live,
}: {
  m: Msgs;
  live: { status: 'ok' | 'partial' | 'error' | 'never'; at: string | null; okAt: string | null };
}) {
  const hhmm = (iso: string | null) =>
    iso ? new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '';
  // The failing state must show when the data was last GOOD, not when the
  // failure happened — otherwise a check that died hours ago reads as fresh.
  const [text, help, tone] =
    live.status === 'ok'
      ? [m.liveOk(hhmm(live.at)), m.liveOkHelp, 'text-fg-subtle']
      : live.status === 'partial'
        ? [m.livePartial(hhmm(live.at)), m.livePartialHelp, 'text-warn']
        : live.status === 'error'
          ? [
              live.okAt ? m.liveError(hhmm(live.okAt)) : m.liveErrorNever,
              m.liveErrorHelp,
              'text-warn',
            ]
          : [m.liveNever, m.liveNeverHelp, 'text-warn'];
  return (
    <p className={`mb-2 text-micro ${tone}`} title={help}>
      {live.status === 'ok' ? '● ' : '▲ '}
      {text}
    </p>
  );
}

function PeopleCard({
  m,
  rows,
  title,
  sub,
  collapsed,
}: {
  m: Msgs;
  rows: PersonRow[];
  title: string;
  sub: string;
  collapsed?: boolean;
}) {
  if (rows.length === 0) return null;
  const table = <PeopleTable m={m} rows={rows} />;
  if (!collapsed) {
    return (
      <Card className="flex flex-col gap-3 p-3 sm:p-4">
        <CardHeading title={`${title} (${rows.length})`} sub={sub} />
        {table}
      </Card>
    );
  }
  return (
    <Card className="p-3 sm:p-4">
      <details className="group">
        <summary className="flex cursor-pointer list-none items-start justify-between gap-3 [&::-webkit-details-marker]:hidden">
          <CardHeading title={`${title} (${rows.length})`} sub={sub} />
          <ChevronDown
            className="mt-1 size-4 shrink-0 text-fg-subtle transition-transform group-open:rotate-180"
            aria-hidden
          />
        </summary>
        <div className="mt-4">{table}</div>
      </details>
    </Card>
  );
}

function PeopleTable({ m, rows }: { m: Msgs; rows: PersonRow[] }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[38rem] text-label">
        <thead>
          <tr className="text-left text-micro text-fg-subtle">
            <th className="w-8 py-1.5" aria-hidden />
            <th className="py-1.5 pr-3 font-medium">{m.colName}</th>
            <th className="py-1.5 pr-3 font-medium">{m.colPhone}</th>
            <th className="py-1.5 pr-3 font-medium">{m.colWaited}</th>
            <th className="py-1.5 font-medium">{m.colBooking}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isOpen = open === row.key;
            return (
              <Fragment key={row.key}>
                <tr
                  className="cursor-pointer border-t border-border hover:bg-surface-2/60"
                  onClick={() => setOpen(isOpen ? null : row.key)}
                >
                  <td className="py-2 pr-1">
                    <ChevronRight
                      aria-label={m.expandRow}
                      className={cn(
                        'size-4 text-fg-subtle transition-transform',
                        isOpen && 'rotate-90',
                      )}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <span className="text-fg">{row.name}</span>
                    {row.doNotCall ? (
                      <Badge className="ml-1.5" tone="bad">
                        🚫 {m.doNotCallBadge}
                      </Badge>
                    ) : row.callbackRequested && !row.booking ? (
                      <Badge className="ml-1.5" tone="warn">
                        📞 {m.callbackBadge}
                      </Badge>
                    ) : null}
                    {/*
                      A live Momence appointment is proof from Momence itself,
                      and it outranks whatever our own HTTP call reported. This
                      alone silences the case that started all this: a lead
                      marked failed on a 502 who was in Momence the whole time.
                    */}
                    {row.momence || row.momenceForward === 'sent' ? null : row.momenceForward ===
                      'uncertain' ? (
                      <Badge className="ml-1.5" tone="warn" title={m.momenceUncertainHelp}>
                        {m.momenceUncertain}
                      </Badge>
                    ) : row.momenceForward === 'rejected' ? (
                      <Badge className="ml-1.5" tone="bad" title={m.momenceRejectedHelp}>
                        {m.momenceRejected}
                      </Badge>
                    ) : (
                      <Badge className="ml-1.5" tone="bad" title={m.momenceNotSentHelp}>
                        {m.momenceNotSent}
                      </Badge>
                    )}
                    {row.source !== 'annet' ? (
                      <span className="ml-1.5 text-micro text-fg-subtle">
                        {sourceLabel(m, row.source)}
                      </span>
                    ) : null}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3">
                    {row.phone && row.doNotCall ? (
                      // No tap-to-call for someone who declined — the number is
                      // shown for identification, not for dialling.
                      <span className="text-fg-muted line-through">{row.phone}</span>
                    ) : row.phone ? (
                      <a
                        href={`tel:${row.phone}`}
                        className="inline-flex items-center gap-1 text-fg hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Phone className="size-3 text-fg-subtle" aria-hidden />
                        {row.phone}
                      </a>
                    ) : (
                      <span className="text-fg-subtle">{EM_DASH}</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 tabular text-fg-muted">
                    {m.hoursShort(row.hoursSinceLead)}
                  </td>
                  <td className="py-2">
                    {row.booking ? (
                      <span className="flex flex-wrap items-center gap-1.5">
                        <Badge tone={row.booking.cancelled ? 'bad' : 'good'}>
                          {row.booking.cancelled ? m.cancelled : fmtSlot(row.booking.slotStartsAt)}
                        </Badge>
                        {row.booking.matchedBy === 'name' ? (
                          <span title={m.matchedByName} className="text-micro text-fg-subtle">
                            ?
                          </span>
                        ) : null}
                      </span>
                    ) : row.momence && !row.momence.cancelled ? (
                      // Booked through Momence or over the phone — our own widget
                      // never saw it, and until now that read as «never answered».
                      <span className="flex flex-wrap items-center gap-1.5">
                        <Badge tone={row.momence.member ? 'good' : 'neutral'}>
                          {fmtSlot(row.momence.slotAt)}
                        </Badge>
                        <span className="text-micro text-fg-subtle">
                          {row.momence.member
                            ? m.becameMember
                            : row.momence.showed
                              ? m.showedUp
                              : new Date(row.momence.slotAt) < new Date()
                                ? m.noShow
                                : m.inMomenceBooked}
                        </span>
                        {row.momence.matchedBy === 'name' ? (
                          <span title={m.matchedByName} className="text-micro text-fg-subtle">
                            ?
                          </span>
                        ) : null}
                      </span>
                    ) : row.priorBooking ? (
                      <span className="text-micro text-fg-subtle" title={m.bookedBefore}>
                        ↺ {m.priorBooking}
                      </span>
                    ) : (
                      <span className="text-fg-subtle">{EM_DASH}</span>
                    )}
                  </td>
                </tr>
                {isOpen ? (
                  <tr className="border-t border-border bg-surface-2/40">
                    <td colSpan={5} className="p-3">
                      <PersonDetail m={m} row={row} />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PersonDetail({ m, row }: { m: Msgs; row: PersonRow }) {
  const answers = row.answers ? parseAnswers(row.answers) : [];
  return (
    <div className="flex flex-col gap-3">
      <p className="text-label text-fg-muted">
        {m.colLead}: <span className="text-fg">{fmtWhen(row.firstLeadAt)}</span>
        {row.submissions > 1 ? (
          <span className="ml-2 text-micro text-fg-subtle">{m.submissionsN(row.submissions)}</span>
        ) : null}
        {row.momenceLeadId ? (
          <span className="ml-2 text-micro text-fg-subtle">Momence #{row.momenceLeadId}</span>
        ) : null}
        {row.email ? <span className="ml-2 text-micro text-fg-subtle">{row.email}</span> : null}
      </p>
      {row.booking ? (
        <p className="text-label text-fg-muted">
          {m.colBooking}: <span className="font-mono text-fg">{row.booking.ref}</span>
          <span className="ml-2 text-fg">{fmtSlot(row.booking.slotStartsAt)}</span>
          <span className="ml-2 text-micro text-fg-subtle">
            ({fmtWhen(row.booking.bookedAt)}
            {row.booking.hoursToBook >= 0 ? ` · ${m.hoursShort(row.booking.hoursToBook)}` : ''})
          </span>
        </p>
      ) : null}
      <div>
        <p className="mb-1 text-micro font-semibold uppercase tracking-wide text-fg-subtle">
          {m.answersTitle}
        </p>
        {answers.length === 0 ? (
          <p className="text-label text-fg-subtle">{m.noAnswers}</p>
        ) : (
          <dl className="grid gap-x-4 gap-y-0.5 sm:grid-cols-2">
            {answers.map((a) => (
              <div key={a.key} className="flex items-baseline gap-2 text-label">
                {a.label ? <dt className="shrink-0 text-fg-subtle">{a.label}:</dt> : null}
                <dd className="text-fg">{a.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  );
}
